import React, { useCallback, useEffect, useState } from 'react';
import {
  CartesianGrid, Line, LineChart, ReferenceLine, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { TrendingDown } from 'lucide-react';
import {
  Badge, Card, Disclaimer, Empty, Loading, Page, PageHead, Stat, Tabs,
} from '../components/ui';
import { api } from '../lib/api';
import { n1, n2, shortDate } from '../lib/format';

const HORIZONS = [7, 14, 30, 60, 90];

const MODELS = [
  { key: 'linear_regression', label: 'Linear regression', color: 'var(--accent)',
    note: 'Least squares fitted to your own history' },
  { key: 'smart', label: 'Smart predictor', color: 'var(--macro-carbs)',
    note: "Holt's damped trend — flattens as adaptation sets in" },
  { key: 'energy_balance', label: 'Energy balance', color: 'var(--ok)',
    note: 'From logged intake against your estimated burn' },
];

const chartAxis = { stroke: 'var(--text-faint)', fontSize: 11, tickLine: false, axisLine: false };

function tooltipStyle() {
  return {
    background: 'var(--surface-2)',
    border: '1px solid var(--border-strong)',
    borderRadius: 10,
    fontSize: 12,
    color: 'var(--text)',
  };
}

export default function WeightAI() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async (horizon) => {
    setLoading(true);
    setError('');
    try { setData(await api.weightAI.predict(horizon)); }
    catch (e) { setError(e.message); setData(null); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(days); }, [days, load]);

  if (loading) return <Page><PageHead title="Weight AI" /><Card><Loading label="Running the models…" /></Card></Page>;

  if (error) {
    return (
      <Page>
        <PageHead title="Weight AI">Forecasts from your own weight history.</PageHead>
        <Card>
          <Empty icon={TrendingDown} title="Not enough data yet">{error}</Empty>
        </Card>
      </Page>
    );
  }

  if (!data) return null;

  const preds = data.predictions || {};
  const details = data.model_details || {};
  const history = data.historical || [];
  const energy = details.energy_balance;

  // One shared time axis: history to the left of today, forecasts to the right.
  const series = [
    ...history.map((w, i) => ({
      x: `D${i - history.length + 1}`,
      actual: w,
    })),
    ...(preds.dates || []).map((d, i) => ({
      x: shortDate(d),
      linear_regression: preds.linear_regression?.[i],
      smart: preds.smart?.[i] ?? preds.lstm?.[i],
      energy_balance: preds.energy_balance?.[i],
    })),
  ];
  // Join the two halves so the lines meet rather than leaving a gap.
  if (series[history.length - 1] && history.length) {
    const last = series[history.length - 1];
    last.linear_regression = data.current_weight;
    last.smart = data.current_weight;
    if (preds.energy_balance) last.energy_balance = data.current_weight;
  }

  const toGo = data.current_weight - data.target_weight;
  const lr = details.linear_regression || {};

  const daysToTarget = (key) => {
    const arr = preds[key];
    if (!arr?.length) return null;
    const idx = arr.findIndex((w) =>
      toGo > 0 ? w <= data.target_weight : w >= data.target_weight
    );
    return idx === -1 ? null : idx + 1;
  };

  return (
    <Page>
      <PageHead
        title="Weight AI"
        actions={
          <Tabs
            value={days}
            onChange={setDays}
            options={HORIZONS.map((d) => ({ value: d, label: `${d}d` }))}
          />
        }
      >
        Three models run on your own logs. Each answers a different question, so read them together
        rather than picking one.
      </PageHead>

      <div className="grid grid-4">
        <Stat label="Current" value={n1(data.current_weight)} unit="kg"
          note={`${history.length} entries logged`} />
        <Stat label="Target" value={n1(data.target_weight)} unit="kg" />
        <Stat
          label={toGo >= 0 ? 'To lose' : 'To gain'}
          value={n1(Math.abs(toGo))} unit="kg"
          tone={Math.abs(toGo) < 0.5 ? 'ok' : undefined}
        />
        <Stat
          label="Trend"
          value={lr.slope_kg_per_day != null ? `${lr.slope_kg_per_day > 0 ? '+' : ''}${n2(lr.slope_kg_per_day)}` : '–'}
          unit="kg/day"
          note={lr.r_squared != null ? `r² ${n2(lr.r_squared)}` : undefined}
          tone={lr.slope_kg_per_day < 0 ? 'ok' : lr.slope_kg_per_day > 0 ? 'warn' : undefined}
        />
      </div>

      <Card title={`${days}-day forecast`} sub="Solid is what you logged; dashed is projected">
        <div style={{ height: 340 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={series} margin={{ top: 8, right: 16, left: -12, bottom: 4 }}>
              <CartesianGrid stroke="var(--border)" strokeDasharray="3 3" vertical={false} />
              <XAxis dataKey="x" {...chartAxis} minTickGap={28} />
              <YAxis {...chartAxis} domain={['dataMin - 1.5', 'dataMax + 1.5']}
                tickFormatter={(v) => v.toFixed(0)} width={44} />
              <Tooltip contentStyle={tooltipStyle()} formatter={(v, k) => [`${n1(v)} kg`, k]} />
              <ReferenceLine y={data.target_weight} stroke="var(--ok)" strokeDasharray="5 4"
                label={{ value: 'Target', fill: 'var(--ok)', fontSize: 11, position: 'insideTopRight' }} />
              <Line type="monotone" dataKey="actual" name="Logged" stroke="var(--text)"
                strokeWidth={2} dot={false} connectNulls={false} />
              {MODELS.map((m) =>
                preds[m.key] ? (
                  <Line key={m.key} type="monotone" dataKey={m.key} name={m.label}
                    stroke={m.color} strokeWidth={2} strokeDasharray="5 4" dot={false}
                    connectNulls={false} />
                ) : null
              )}
            </LineChart>
          </ResponsiveContainer>
        </div>
        <div className="macro-legend" style={{ marginTop: 12 }}>
          <span><i style={{ background: 'var(--text)' }} />Logged</span>
          {MODELS.filter((m) => preds[m.key]).map((m) => (
            <span key={m.key}><i style={{ background: m.color }} />{m.label}</span>
          ))}
        </div>
      </Card>

      <div className="grid grid-3">
        {MODELS.map((m) => {
          const arr = preds[m.key];
          if (!arr?.length) {
            return (
              <Card key={m.key} title={m.label} sub={m.note}>
                <Empty title="Not available">
                  {m.key === 'energy_balance'
                    ? 'Log food for at least 7 separate days to project from energy balance.'
                    : 'Needs more weight entries.'}
                </Empty>
              </Card>
            );
          }
          const eta = daysToTarget(m.key);
          return (
            <Card key={m.key} title={m.label} sub={m.note}>
              <div className="spread" style={{ alignItems: 'flex-end' }}>
                <div>
                  <div className="label">In {days} days</div>
                  <div className="num" style={{ fontSize: 26, fontWeight: 700, color: m.color, marginTop: 4 }}>
                    {n1(arr[arr.length - 1])}<span style={{ fontSize: 13, color: 'var(--text-dim)' }}> kg</span>
                  </div>
                </div>
                <Badge tone={eta ? 'ok' : 'muted'}>
                  {eta ? `Target in ${eta}d` : 'Target not reached'}
                </Badge>
              </div>

              {m.key === 'smart' && details.smart && (
                <p className="faint" style={{ fontSize: 12, marginTop: 12 }}>
                  Settles toward <span className="num">{n1(details.smart.asymptote_kg)}</span> kg
                  (α {details.smart.alpha} · β {details.smart.beta} · φ {details.smart.phi}).
                </p>
              )}
              {m.key === 'linear_regression' && (
                <p className="faint" style={{ fontSize: 12, marginTop: 12 }}>
                  Straight-line extrapolation — realistic near term, optimistic far out.
                </p>
              )}
              {m.key === 'energy_balance' && energy && (
                <p className="faint" style={{ fontSize: 12, marginTop: 12 }}>
                  <span className="num">{energy.mean_daily_intake_kcal}</span> kcal eaten against an
                  estimated <span className="num">{energy.estimated_tdee_kcal}</span> kcal burn —
                  a <span className="num">{Math.abs(energy.measured_deficit_kcal ?? energy.daily_deficit_kcal)}</span> kcal daily{' '}
                  {(energy.measured_deficit_kcal ?? energy.daily_deficit_kcal) >= 0 ? 'deficit' : 'surplus'}
                  {energy.days_counted && energy.days_counted !== energy.days_logged
                    ? <> across <span className="num">{energy.days_counted}</span> fully logged days
                        of {energy.days_logged}.</>
                    : <> over {energy.days_logged} logged days.</>}
                  {energy.deficit_capped && (
                    <>
                      {' '}The projection uses{' '}
                      <span className="num">{Math.abs(energy.daily_deficit_kcal)}</span> kcal:
                      a gap that large is not held for long, and the forecast is damped for
                      adaptation rather than extrapolated.
                    </>
                  )}
                </p>
              )}
            </Card>
          );
        })}
      </div>

      <Card title="Projection by horizon" flush>
        <div className="table-wrap">
          <table className="data">
            <thead>
              <tr>
                <th>Horizon</th>
                {MODELS.filter((m) => preds[m.key]).map((m) => (
                  <th key={m.key} className="n">{m.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {HORIZONS.filter((h) => h <= days).map((h) => (
                <tr key={h}>
                  <td style={{ fontWeight: 600 }}>{h} days</td>
                  {MODELS.filter((m) => preds[m.key]).map((m) => (
                    <td key={m.key} className="n" style={{ color: m.color }}>
                      {n1(preds[m.key][h - 1])}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ padding: '12px 16px 14px' }}>
          <Disclaimer>
            Projections assume your current habits continue. They are estimates, not targets, and do
            not replace advice from a doctor or dietitian.
          </Disclaimer>
        </div>
      </Card>
    </Page>
  );
}
